import { useState, useEffect, useMemo, useRef } from "react";
import { useApp } from "../context/AppContext";
import { Icons } from "../components/Icons";

// ─── Helpers ───────────────────────────────────────────────
const scoreColor = (v) => v >= 90 ? "#0cce6b" : v >= 70 ? "#a3cc34" : v >= 50 ? "#ffa400" : "#ff4e42";
const scoreBg = (v) => v >= 90 ? "rgba(12,206,107,0.10)" : v >= 70 ? "rgba(163,204,52,0.10)" : v >= 50 ? "rgba(255,164,0,0.10)" : "rgba(255,78,66,0.10)";

const SEVERITY = {
  critical: { label: "Критично", color: "#ef4444", bg: "rgba(239,68,68,0.10)" },
  high:     { label: "Високо",  color: "#f59e0b", bg: "rgba(245,158,11,0.10)" },
  medium:   { label: "Середньо", color: "#eab308", bg: "rgba(234,179,8,0.10)" },
  low:      { label: "Низько",  color: "#3b82f6", bg: "rgba(59,130,246,0.10)" },
  info:     { label: "Інфо",     color: "#6b7280", bg: "rgba(107,114,128,0.10)" },
};
const CATEGORY = {
  tech:          { label: "Технічний SEO", icon: "settings", weight: 25 },
  cwv:           { label: "Швидкість (CWV)", icon: "speed",    weight: 20 },
  seo:           { label: "Семантика/Контент", icon: "key",     weight: 20 },
  backlinks:     { label: "Беклінки",       icon: "link",     weight: 15 },
  gsc:           { label: "Search Console", icon: "search",   weight: 15 },
  ai_visibility: { label: "AI Видимість",   icon: "robot",    weight: 5 },
};

// Простий markdown → HTML (як у ReportPage)
function md2html(md) {
  if (!md) return "";
  let h = md
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    .replace(/^### (.*$)/gm, "<h3>$1</h3>")
    .replace(/^## (.*$)/gm, "<h2>$1</h2>")
    .replace(/^# (.*$)/gm, "<h2>$1</h2>")
    .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
    .replace(/\*([^*]+)\*/g, "<em>$1</em>")
    .replace(/`([^`]+)`/g, "<code style='background:rgba(99,102,241,0.10);padding:1px 4px;border-radius:3px;font-size:90%'>$1</code>")
    .replace(/^- (.*$)/gm, "<li>$1</li>")
    .replace(/(<li>.*<\/li>\s*)+/gs, "<ul style='padding-left:20px;margin:8px 0'>$&</ul>")
    .replace(/\n\n/g, "</p><p style='margin:8px 0'>");
  return "<p style='margin:8px 0'>" + h + "</p>";
}

const ScoreCircle = ({ value, size = 60, label }) => {
  const color = scoreColor(value);
  const r = (size - 6) / 2;
  const circ = 2 * Math.PI * r;
  const offset = circ - (value / 100) * circ;
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
      <svg width={size} height={size} style={{ transform: "rotate(-90deg)" }}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="rgba(128,128,128,0.15)" strokeWidth={5} />
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={5}
          strokeDasharray={circ} strokeDashoffset={offset} strokeLinecap="round"
          style={{ transition: "stroke-dashoffset 0.6s ease" }} />
        <text x={size / 2} y={size / 2} textAnchor="middle" dominantBaseline="central"
          style={{ transform: "rotate(90deg)", transformOrigin: "center", fontSize: size * 0.28, fontWeight: 800, fill: color }}>{value}</text>
      </svg>
      {label && <div style={{ fontSize: 11, color: "#888", textAlign: "center" }}>{label}</div>}
    </div>
  );
};

// ─── PDF builder (HTML string for window.print) ────────────
function buildPdfHtml({ run, findings, project }) {
  const date = new Date(run.started_at).toLocaleDateString("uk-UA", { year: "numeric", month: "long", day: "numeric" });
  const sCol = scoreColor(run.score_overall);
  const cats = ["tech", "cwv", "seo", "backlinks", "gsc"];

  const aiCritical = run.ai_top_critical || [];
  const aiWins = run.ai_top_wins || [];
  const plan = run.ai_action_plan || { "30d": [], "60d": [], "90d": [] };
  const narrative = run.ai_narrative || {};

  const sevSort = { critical: 1, high: 2, medium: 3, low: 4, info: 5 };
  const sortedFindings = [...findings].sort((a, b) => (sevSort[a.severity] || 9) - (sevSort[b.severity] || 9));

  return `<!DOCTYPE html><html lang="uk"><head><meta charset="UTF-8"><title>SEO Audit 360 — ${project.name}</title>
<style>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&display=swap');
@page{size:A4;margin:15mm 18mm}
@media print { .page-break {page-break-before: always} .no-print {display:none} body {-webkit-print-color-adjust:exact; print-color-adjust:exact} }
*{box-sizing:border-box;margin:0;padding:0}
body{font-family:'Inter',Arial,sans-serif;font-size:11px;color:#1a1a2e;line-height:1.55}
.doc{max-width:780px;margin:0 auto}
.cover{height:230mm;display:flex;flex-direction:column;justify-content:center;align-items:center;text-align:center;page-break-after:always}
.cover .logo-row{display:flex;align-items:center;gap:14px;margin-bottom:24px}
.cover h1{font-size:42px;font-weight:800;letter-spacing:-1px;margin-bottom:8px}
.cover .domain{font-size:18px;color:#6366f1;margin-bottom:60px}
.cover .score-big{font-size:140px;font-weight:800;line-height:1;color:${sCol}}
.cover .score-suffix{font-size:36px;color:#888}
.cover .score-label{font-size:14px;color:#888;margin-top:6px}
.cover .date{font-size:11px;color:#888;margin-top:60px}
.section{margin:24px 0}
.section h2{font-size:20px;font-weight:700;border-bottom:2px solid #6366f1;padding-bottom:6px;margin-bottom:12px;color:#1a1a2e}
.section h3{font-size:14px;font-weight:700;margin:14px 0 6px;color:#1a1a2e}
.section p{margin:6px 0}
.section ul{padding-left:20px;margin:8px 0}
.section li{margin:3px 0}
.summary-grid{display:grid;grid-template-columns:repeat(5,1fr);gap:8px;margin:14px 0}
.score-card{padding:12px;border-radius:8px;text-align:center;border:1px solid #e5e7eb}
.score-card .v{font-size:28px;font-weight:800;line-height:1}
.score-card .l{font-size:10px;color:#888;margin-top:4px}
.finding{border-left:4px solid #6366f1;padding:8px 12px;margin:6px 0;background:#fafafa;border-radius:0 4px 4px 0}
.finding .ft{font-weight:700;font-size:12px;margin-bottom:3px}
.finding .fd{font-size:11px;color:#555;margin:2px 0}
.finding .fr{font-size:11px;color:#1a1a2e;background:rgba(99,102,241,0.06);padding:4px 8px;border-radius:4px;margin-top:4px}
.sev-critical{border-color:#ef4444;background:rgba(239,68,68,0.04)}
.sev-high{border-color:#f59e0b;background:rgba(245,158,11,0.04)}
.sev-medium{border-color:#eab308;background:rgba(234,179,8,0.04)}
.sev-low{border-color:#3b82f6;background:rgba(59,130,246,0.04)}
.sev-info{border-color:#6b7280;background:rgba(107,114,128,0.04)}
.win-table{width:100%;border-collapse:collapse;margin-top:8px;font-size:10.5px}
.win-table th{background:#6366f1;color:#fff;padding:6px 8px;text-align:left;font-weight:600}
.win-table td{padding:6px 8px;border-bottom:1px solid #e5e7eb}
.win-table tr:nth-child(even) td{background:#fafafa}
.eff-low{color:#0cce6b;font-weight:600}
.eff-medium{color:#ffa400;font-weight:600}
.eff-high{color:#ff4e42;font-weight:600}
.plan-col{padding:14px;border-radius:8px;background:rgba(99,102,241,0.04);border:1px solid rgba(99,102,241,0.15)}
.plan-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:10px;margin-top:8px}
.foot{margin-top:32px;padding:16px;border-top:2px solid #6366f1;display:flex;justify-content:space-between;align-items:center}
.foot .powered{display:flex;align-items:center;gap:6px;font-size:11px;color:#666}
.cat-header{margin-top:16px;padding:8px 12px;background:rgba(99,102,241,0.06);border-radius:6px;font-weight:700;color:#6366f1}
</style></head>
<body><div class="doc">

<!-- Cover -->
<div class="cover">
  <div class="logo-row">
    <img src="${window.location.origin}/svitlo-logo.png" style="height:48px" onerror="this.style.display='none'"/>
    <img src="${window.location.origin}/logo.png" style="height:48px" onerror="this.style.display='none'"/>
  </div>
  <h1>SEO Audit 360</h1>
  <div class="domain">${project.name}</div>
  <div class="domain" style="font-size:14px;color:#888">${project.domain || ""}</div>
  <div style="margin-top:40px">
    <span class="score-big">${run.score_overall}</span><span class="score-suffix">/100</span>
  </div>
  <div class="score-label">Загальний Health Score</div>
  <div class="date">${date}</div>
</div>

<!-- Executive Summary -->
<div class="section">
  <h2>Executive Summary</h2>
  ${md2html(run.ai_summary_md || "Резюме недоступне.")}
</div>

<!-- Score breakdown -->
<div class="section">
  <h3>Розбивка по категоріях</h3>
  <div class="summary-grid">
    ${cats.map(c => `
      <div class="score-card" style="border-color:${scoreColor(run["score_" + c])};background:${scoreBg(run["score_" + c])}">
        <div class="v" style="color:${scoreColor(run["score_" + c])}">${run["score_" + c]}</div>
        <div class="l">${CATEGORY[c].label}</div>
      </div>`).join("")}
  </div>
</div>

<div class="page-break"></div>

<!-- Top Critical -->
<div class="section">
  <h2>Топ критичних проблем</h2>
  ${aiCritical.length === 0
    ? "<p style='color:#666;font-style:italic'>Критичних проблем не виявлено.</p>"
    : aiCritical.map((c, i) => `
      <div class="finding sev-critical">
        <div class="ft">${i + 1}. ${c.title || "—"}</div>
        <div class="fd">${c.description || ""}</div>
        ${c.recommendation ? `<div class="fr"><strong>Рекомендація:</strong> ${c.recommendation}</div>` : ""}
      </div>`).join("")}
</div>

<!-- Quick Wins -->
<div class="section">
  <h2>Top Quick Wins (швидкі покращення)</h2>
  ${aiWins.length === 0
    ? "<p style='color:#666;font-style:italic'>Не виявлено швидких можливостей.</p>"
    : `<table class="win-table">
      <thead><tr><th style="width:55%">Що зробити</th><th>Impact</th><th>Effort</th></tr></thead>
      <tbody>${aiWins.map(w => `
        <tr>
          <td><strong>${w.title || "—"}</strong><br/><span style="font-size:10px;color:#666">${w.recommendation || w.description || ""}</span></td>
          <td>${w.impact || "—"}</td>
          <td class="eff-${w.effort || "medium"}">${(w.effort || "medium").toUpperCase()}</td>
        </tr>`).join("")}</tbody></table>`}
</div>

<div class="page-break"></div>

<!-- 30/60/90 Plan -->
<div class="section">
  <h2>План дій 30/60/90 днів</h2>
  <div class="plan-grid">
    <div class="plan-col">
      <h3 style="margin-top:0;color:#6366f1">Перші 30 днів</h3>
      <ul>${(plan["30d"] || []).map(x => `<li>${x}</li>`).join("") || "<li><em>немає</em></li>"}</ul>
    </div>
    <div class="plan-col">
      <h3 style="margin-top:0;color:#6366f1">31-60 днів</h3>
      <ul>${(plan["60d"] || []).map(x => `<li>${x}</li>`).join("") || "<li><em>немає</em></li>"}</ul>
    </div>
    <div class="plan-col">
      <h3 style="margin-top:0;color:#6366f1">61-90 днів</h3>
      <ul>${(plan["90d"] || []).map(x => `<li>${x}</li>`).join("") || "<li><em>немає</em></li>"}</ul>
    </div>
  </div>
</div>

<!-- Narrative per category -->
${cats.filter(c => narrative[c]).length > 0 ? `
<div class="page-break"></div>
<div class="section">
  <h2>Детальний аналіз по категоріях</h2>
  ${cats.filter(c => narrative[c]).map(c => `
    <h3>${CATEGORY[c].label} (${run["score_" + c]}/100)</h3>
    ${md2html(narrative[c])}
  `).join("")}
</div>` : ""}

<!-- Detailed findings -->
<div class="page-break"></div>
<div class="section">
  <h2>Всі знахідки (${findings.length})</h2>
  ${["critical", "high", "medium", "low", "info"].map(sev => {
    const sf = sortedFindings.filter(f => f.severity === sev);
    if (!sf.length) return "";
    return `<div class="cat-header">${SEVERITY[sev].label} — ${sf.length}</div>
    ${sf.map(f => `
      <div class="finding sev-${sev}">
        <div class="ft">${f.title}${f.url ? ` <span style="font-weight:400;color:#888;font-size:10px">(${f.url})</span>` : ""}</div>
        ${f.description ? `<div class="fd">${f.description}</div>` : ""}
        ${f.recommendation ? `<div class="fr">→ ${f.recommendation}</div>` : ""}
      </div>`).join("")}`;
  }).join("")}
</div>

<!-- Footer -->
<div class="foot">
  <div class="powered">
    <img src="${window.location.origin}/svitlo-logo.png" style="height:24px" onerror="this.style.display='none'"/>
    <strong>Svitlo Agency</strong> · виконано через
    <img src="${window.location.origin}/logo.png" style="height:18px" onerror="this.style.display='none'"/>
    <strong style="color:#6366f1">Spalah</strong>
  </div>
  <div style="font-size:10px;color:#888">${date}</div>
</div>

</div></body></html>`;
}

// ─── Main view ─────────────────────────────────────────────
export default function AuditView() {
  const { proj, activeId, apiFetch, flash, dark, t, card, btnP, btnG, btnSm } = useApp();
  if (!proj) return null;

  const [runs, setRuns] = useState([]);
  const [run, setRun] = useState(null);          // currently displayed run + findings
  const [findings, setFindings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(null);  // {taskId, runId, progress}
  const [scope, setScope] = useState({
    includeTech: true,
    includeCwv: true,
    includeSeo: true,
    includeBacklinks: true,
    includeGsc: true,
    includeAiVisibility: false,
    refreshSiteScan: false,
    refreshPagespeed: false,
  });
  const [filterSev, setFilterSev] = useState("all");
  const [filterCat, setFilterCat] = useState("all");
  const [shareModal, setShareModal] = useState(null);
  const pollRef = useRef(null);

  // Load runs list
  const loadRuns = async () => {
    setLoading(true);
    try {
      const r = await apiFetch(`/api/projects/${activeId}/audit`);
      const data = await r.json();
      if (Array.isArray(data)) {
        setRuns(data);
        // Auto-load latest done run
        const latestDone = data.find(x => x.status === "done");
        if (latestDone) await loadRun(latestDone.id);
      }
    } catch (e) {
      flash("Помилка завантаження: " + e.message);
    }
    setLoading(false);
  };

  const loadRun = async (runId) => {
    try {
      const r = await apiFetch(`/api/projects/${activeId}/audit/${runId}`);
      const data = await r.json();
      setRun(data.run);
      setFindings(data.findings || []);
    } catch (e) {
      flash("Помилка завантаження run: " + e.message);
    }
  };

  useEffect(() => { if (activeId) loadRuns(); }, [activeId]);

  // Polling task progress
  useEffect(() => {
    if (!running?.taskId) return;
    pollRef.current = setInterval(async () => {
      try {
        const r = await apiFetch(`/api/tasks/${running.taskId}`);
        const task = await r.json();
        setRunning(p => ({ ...p, progress: task.progress || {} }));
        if (task.status === "done") {
          clearInterval(pollRef.current);
          await loadRuns();          // reload list and auto-show latest
          await loadRun(running.runId);
          setRunning(null);
          flash("Аудит завершено");
        } else if (task.status === "error") {
          clearInterval(pollRef.current);
          setRunning(null);
          flash("Помилка аудиту: " + (task.error || "невідомо"));
          await loadRuns();
        }
      } catch (e) {
        // ignore single failed poll
      }
    }, 1500);
    return () => clearInterval(pollRef.current);
  }, [running?.taskId]);

  const startAudit = async () => {
    try {
      const r = await apiFetch(`/api/projects/${activeId}/audit/run`, {
        method: "POST",
        body: JSON.stringify({ scope }),
      });
      const data = await r.json();
      if (data.error) {
        flash(data.error);
        return;
      }
      setRunning({ taskId: data.taskId, runId: data.runId, progress: {} });
      flash("Аудит запущено");
    } catch (e) {
      flash("Помилка запуску: " + e.message);
    }
  };

  const deleteRun = async (runId) => {
    if (!confirm("Видалити цей аудит?")) return;
    try {
      await apiFetch(`/api/projects/${activeId}/audit/${runId}`, { method: "DELETE" });
      flash("Видалено");
      if (run?.id === runId) { setRun(null); setFindings([]); }
      await loadRuns();
    } catch (e) {
      flash("Помилка: " + e.message);
    }
  };

  const generatePDF = () => {
    if (!run) return;
    const html = buildPdfHtml({ run, findings, project: { name: proj.name, domain: proj.domain } });
    const win = window.open("", "_blank");
    if (!win) {
      flash("Дозвольте pop-up для друку PDF");
      return;
    }
    win.document.write(html);
    win.document.close();
    setTimeout(() => win.print(), 700);
  };

  const sharePublic = async () => {
    if (!run) return;
    try {
      const r = await apiFetch(`/api/projects/${activeId}/audit/${run.id}/share`, { method: "POST" });
      const data = await r.json();
      if (data.error) { flash(data.error); return; }
      const fullUrl = `${window.location.origin}/audit/${data.token}`;
      setShareModal({ url: fullUrl, token: data.token });
    } catch (e) {
      flash("Помилка: " + e.message);
    }
  };

  const copyShareUrl = () => {
    if (!shareModal?.url) return;
    navigator.clipboard.writeText(shareModal.url).then(() => flash("Скопійовано"));
  };

  // ─── Filters ───
  const filteredFindings = useMemo(() => {
    return findings.filter(f =>
      (filterSev === "all" || f.severity === filterSev) &&
      (filterCat === "all" || f.category === filterCat)
    );
  }, [findings, filterSev, filterCat]);

  const aiCritical = run?.ai_top_critical || [];
  const aiWins = run?.ai_top_wins || [];
  const aiPlan = run?.ai_action_plan || { "30d": [], "60d": [], "90d": [] };
  const aiNarrative = run?.ai_narrative || {};

  // ─── Render ───────────────────────────────────────────
  if (loading) {
    return <div style={{ textAlign: "center", padding: 40, color: t.textMuted }}>Завантаження…</div>;
  }

  return (
    <div>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
        <h2 style={{ fontSize: 20, fontWeight: 700, margin: 0, display: "flex", alignItems: "center", gap: 8 }}>
          {Icons.shield(20)} SEO Audit 360
        </h2>
        {run && (
          <div style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
            <button onClick={generatePDF} style={{ ...btnG, fontSize: 12, display: "flex", alignItems: "center", gap: 6 }}>
              {Icons.download(14)} PDF
            </button>
            <button onClick={sharePublic} style={{ ...btnG, fontSize: 12, display: "flex", alignItems: "center", gap: 6 }}>
              {Icons.link(14)} Поділитись
            </button>
            <button onClick={startAudit} disabled={!!running} style={{ ...btnP, fontSize: 12, opacity: running ? 0.5 : 1, display: "flex", alignItems: "center", gap: 6 }}>
              {Icons.refresh(14)} Новий аудит
            </button>
          </div>
        )}
      </div>

      {/* Running state */}
      {running && (
        <div style={{ ...card, padding: 20, marginBottom: 16, background: "rgba(99,102,241,0.05)", border: "1px solid rgba(99,102,241,0.20)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
            {Icons.activity(20, "#6366f1")}
            <strong>Аудит виконується…</strong>
          </div>
          <div style={{ fontSize: 12, color: "#666", marginBottom: 8 }}>
            Фаза: <strong>{running.progress?.phase || "ініціалізація"}</strong>
            {running.progress?.done != null && (
              <span> · Прогрес: {running.progress.done}/{running.progress.total}</span>
            )}
          </div>
          <div style={{ height: 6, background: "rgba(99,102,241,0.15)", borderRadius: 3, overflow: "hidden" }}>
            <div style={{
              height: "100%",
              width: running.progress?.total ? `${(running.progress.done / running.progress.total * 100)}%` : "0%",
              background: "#6366f1",
              transition: "width 0.4s ease"
            }} />
          </div>
        </div>
      )}

      {/* Empty state — no runs */}
      {!run && !running && (
        <div style={{ ...card, padding: 30, textAlign: "center" }}>
          <div style={{ fontSize: 14, color: t.textMuted, marginBottom: 16 }}>
            Натисніть «Запустити» — пройдемо по 5 категоріях:<br/>
            <strong style={{ color: t.text }}>Тех. SEO · Швидкість · Семантика · Беклінки · GSC</strong>
            <br/>і отримаємо AI-резюме + 30/60/90-day plan + експорт у PDF.
          </div>

          <div style={{ textAlign: "left", maxWidth: 480, margin: "20px auto", padding: 16, background: dark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.02)", borderRadius: 8 }}>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Налаштування аудиту</div>
            <label style={{ display: "flex", alignItems: "center", gap: 8, padding: 4, fontSize: 12, cursor: "pointer" }}>
              <input type="checkbox" checked={scope.refreshSiteScan} onChange={e => setScope(s => ({ ...s, refreshSiteScan: e.target.checked }))} />
              Пересканувати сайт перед аудитом <span style={{ color: "#888" }}>(+2-3 хв)</span>
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: 8, padding: 4, fontSize: 12, cursor: "pointer" }}>
              <input type="checkbox" checked={scope.refreshPagespeed} onChange={e => setScope(s => ({ ...s, refreshPagespeed: e.target.checked }))} />
              Оновити PageSpeed дані <span style={{ color: "#888" }}>(+1-2 хв)</span>
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: 8, padding: 4, fontSize: 12, cursor: "pointer", color: "#a855f7" }}>
              <input type="checkbox" checked={scope.includeAiVisibility} onChange={e => setScope(s => ({ ...s, includeAiVisibility: e.target.checked }))} />
              Додати AI Видимість <strong>+$5</strong> <span style={{ color: "#888", fontWeight: 400 }}>(ChatGPT/Claude цитування)</span>
            </label>
          </div>

          <button onClick={startAudit} style={{ ...btnP, fontSize: 14, padding: "10px 24px", display: "inline-flex", alignItems: "center", gap: 8 }}>
            {Icons.play(16)} Запустити аудит
          </button>

          {runs.length > 0 && (
            <div style={{ marginTop: 30, textAlign: "left" }}>
              <h3 style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Попередні аудити</h3>
              {runs.map(r => (
                <div key={r.id} onClick={() => loadRun(r.id)} style={{
                  display: "flex", gap: 10, alignItems: "center", padding: 8,
                  borderRadius: 6, cursor: "pointer",
                  background: dark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)",
                  marginBottom: 4,
                }}>
                  <div style={{ color: scoreColor(r.score_overall), fontWeight: 800, fontSize: 18, width: 40 }}>
                    {r.score_overall || "—"}
                  </div>
                  <div style={{ flex: 1, fontSize: 12 }}>
                    <div>{new Date(r.started_at).toLocaleString("uk-UA")}</div>
                    <div style={{ color: "#888", fontSize: 11 }}>{r.status}</div>
                  </div>
                  <button onClick={(e) => { e.stopPropagation(); deleteRun(r.id); }} style={{ ...btnSm, fontSize: 11 }}>
                    {Icons.trash(12)}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Result state */}
      {run && run.status === "done" && (
        <div>
          {/* Hero scores */}
          <div style={{ ...card, padding: 20, marginBottom: 16, display: "flex", gap: 20, alignItems: "center" }}>
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: 64, fontWeight: 800, color: scoreColor(run.score_overall), lineHeight: 1 }}>
                {run.score_overall}
              </div>
              <div style={{ fontSize: 11, color: "#888" }}>Health Score / 100</div>
            </div>
            <div style={{ display: "flex", gap: 14, flexWrap: "wrap", flex: 1 }}>
              {["tech", "cwv", "seo", "backlinks", "gsc"].map(c => (
                <ScoreCircle key={c} value={run["score_" + c]} size={56} label={CATEGORY[c].label} />
              ))}
            </div>
            <div style={{ fontSize: 11, color: "#888", textAlign: "right" }}>
              {new Date(run.started_at).toLocaleString("uk-UA")}
              <br/>{findings.length} знахідок
            </div>
          </div>

          {/* AI Executive Summary */}
          {run.ai_summary_md && (
            <div style={{ ...card, padding: 20, marginBottom: 16 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                {Icons.fileText(18, "#6366f1")}
                <h3 style={{ fontSize: 16, margin: 0 }}>Executive Summary</h3>
              </div>
              <div style={{ fontSize: 13, lineHeight: 1.6 }} dangerouslySetInnerHTML={{ __html: md2html(run.ai_summary_md) }} />
            </div>
          )}

          {/* Top Critical */}
          {aiCritical.length > 0 && (
            <div style={{ ...card, padding: 20, marginBottom: 16 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                {Icons.alertCircle(18, "#ef4444")}
                <h3 style={{ fontSize: 16, margin: 0 }}>Топ критичних проблем</h3>
              </div>
              {aiCritical.map((c, i) => (
                <div key={i} style={{ padding: 12, marginBottom: 8, background: "rgba(239,68,68,0.04)", borderLeft: "4px solid #ef4444", borderRadius: "0 6px 6px 0" }}>
                  <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 4 }}>{i + 1}. {c.title}</div>
                  {c.description && <div style={{ fontSize: 12, color: "#555", marginBottom: 6 }}>{c.description}</div>}
                  {c.recommendation && (
                    <div style={{ fontSize: 12, padding: "6px 10px", background: "rgba(99,102,241,0.06)", borderRadius: 4 }}>
                      <strong>Рекомендація:</strong> {c.recommendation}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Top Wins */}
          {aiWins.length > 0 && (
            <div style={{ ...card, padding: 20, marginBottom: 16 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                {Icons.zap(18, "#0cce6b")}
                <h3 style={{ fontSize: 16, margin: 0 }}>Quick Wins</h3>
              </div>
              <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ background: dark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.03)" }}>
                    <th style={{ padding: 8, textAlign: "left" }}>Що зробити</th>
                    <th style={{ padding: 8, width: 70 }}>Impact</th>
                    <th style={{ padding: 8, width: 80 }}>Effort</th>
                  </tr>
                </thead>
                <tbody>
                  {aiWins.map((w, i) => (
                    <tr key={i} style={{ borderBottom: "1px solid " + t.border }}>
                      <td style={{ padding: 8 }}>
                        <strong>{w.title}</strong>
                        {w.recommendation && <div style={{ fontSize: 11, color: "#777", marginTop: 2 }}>{w.recommendation}</div>}
                      </td>
                      <td style={{ padding: 8, textAlign: "center", fontWeight: 700, color: scoreColor(w.impact || 0) }}>
                        {w.impact || "—"}
                      </td>
                      <td style={{ padding: 8, textAlign: "center" }}>
                        <span style={{
                          padding: "2px 6px", borderRadius: 4, fontSize: 10, fontWeight: 700,
                          color: w.effort === "low" ? "#0cce6b" : w.effort === "high" ? "#ff4e42" : "#ffa400",
                          background: w.effort === "low" ? "rgba(12,206,107,0.10)" : w.effort === "high" ? "rgba(255,78,66,0.10)" : "rgba(255,164,0,0.10)",
                        }}>
                          {(w.effort || "medium").toUpperCase()}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* 30/60/90 Plan */}
          {(aiPlan["30d"]?.length || aiPlan["60d"]?.length || aiPlan["90d"]?.length) ? (
            <div style={{ ...card, padding: 20, marginBottom: 16 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                {Icons.calendar(18, "#a855f7")}
                <h3 style={{ fontSize: 16, margin: 0 }}>План дій 30/60/90 днів</h3>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12 }}>
                {["30d", "60d", "90d"].map(p => (
                  <div key={p} style={{ padding: 12, background: "rgba(99,102,241,0.04)", border: "1px solid rgba(99,102,241,0.15)", borderRadius: 8 }}>
                    <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 6, color: "#6366f1" }}>
                      {p === "30d" ? "Перші 30 днів" : p === "60d" ? "31-60 днів" : "61-90 днів"}
                    </div>
                    <ul style={{ paddingLeft: 16, margin: 0, fontSize: 12, lineHeight: 1.5 }}>
                      {(aiPlan[p] || []).map((x, i) => <li key={i} style={{ marginBottom: 3 }}>{x}</li>)}
                      {!(aiPlan[p]?.length) && <li style={{ color: "#888", fontStyle: "italic" }}>немає</li>}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          {/* Findings filters + list */}
          <div style={{ ...card, padding: 20, marginBottom: 16 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
              <h3 style={{ fontSize: 16, margin: 0, flex: 1 }}>Знахідки ({filteredFindings.length} з {findings.length})</h3>
              <select value={filterSev} onChange={e => setFilterSev(e.target.value)} style={{ ...btnSm, fontSize: 11, padding: "4px 8px" }}>
                <option value="all">Всі severity</option>
                {Object.entries(SEVERITY).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
              <select value={filterCat} onChange={e => setFilterCat(e.target.value)} style={{ ...btnSm, fontSize: 11, padding: "4px 8px" }}>
                <option value="all">Всі категорії</option>
                {Object.entries(CATEGORY).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </div>

            <div style={{ maxHeight: 600, overflow: "auto" }}>
              {filteredFindings.length === 0 ? (
                <div style={{ textAlign: "center", padding: 30, color: "#888", fontSize: 12 }}>
                  Немає знахідок за обраними фільтрами
                </div>
              ) : filteredFindings.map((f) => (
                <div key={f.id} style={{
                  padding: 10, marginBottom: 6, borderLeft: "4px solid " + SEVERITY[f.severity].color,
                  background: SEVERITY[f.severity].bg, borderRadius: "0 6px 6px 0"
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 3 }}>
                    <span style={{
                      fontSize: 9, fontWeight: 700, color: SEVERITY[f.severity].color,
                      padding: "2px 6px", borderRadius: 3, background: "rgba(255,255,255,0.7)",
                    }}>{SEVERITY[f.severity].label}</span>
                    <span style={{ fontSize: 10, color: "#888" }}>{CATEGORY[f.category]?.label || f.category}</span>
                    {f.url && <span style={{ fontSize: 10, color: "#777", marginLeft: "auto" }}>{f.url}</span>}
                  </div>
                  <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 2 }}>{f.title}</div>
                  {f.description && <div style={{ fontSize: 11, color: "#555", marginBottom: 4 }}>{f.description}</div>}
                  {f.recommendation && (
                    <div style={{ fontSize: 11, padding: "4px 8px", background: "rgba(99,102,241,0.05)", borderRadius: 3 }}>
                      → {f.recommendation}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* History */}
          {runs.length > 1 && (
            <div style={{ ...card, padding: 20 }}>
              <h3 style={{ fontSize: 14, marginTop: 0, marginBottom: 10 }}>Попередні аудити</h3>
              {runs.map(r => (
                <div key={r.id} onClick={() => loadRun(r.id)} style={{
                  display: "flex", gap: 10, alignItems: "center", padding: 6,
                  borderRadius: 4, cursor: "pointer",
                  background: r.id === run.id ? "rgba(99,102,241,0.10)" : "transparent",
                }}>
                  <div style={{ color: scoreColor(r.score_overall), fontWeight: 700, fontSize: 14, width: 32 }}>
                    {r.score_overall || "—"}
                  </div>
                  <div style={{ flex: 1, fontSize: 11 }}>
                    {new Date(r.started_at).toLocaleString("uk-UA")} · {r.status}
                  </div>
                  <button onClick={(e) => { e.stopPropagation(); deleteRun(r.id); }} style={{ ...btnSm, fontSize: 10 }}>×</button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Error state */}
      {run && run.status === "error" && (
        <div style={{ ...card, padding: 20, background: "rgba(239,68,68,0.05)", border: "1px solid rgba(239,68,68,0.20)" }}>
          <h3 style={{ color: "#ef4444", marginTop: 0 }}>Помилка аудиту</h3>
          <pre style={{ fontSize: 11, color: "#666", whiteSpace: "pre-wrap" }}>{run.error}</pre>
          <button onClick={startAudit} style={{ ...btnP, fontSize: 12 }}>Спробувати ще раз</button>
        </div>
      )}

      {/* Share modal */}
      {shareModal && (
        <div onClick={() => setShareModal(null)} style={{
          position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
          background: "rgba(0,0,0,0.5)", zIndex: 1000,
          display: "flex", alignItems: "center", justifyContent: "center"
        }}>
          <div onClick={e => e.stopPropagation()} style={{
            background: t.bg, padding: 24, borderRadius: 12, maxWidth: 500, width: "90%",
            border: "1px solid " + t.border,
          }}>
            <h3 style={{ marginTop: 0 }}>Публічне посилання</h3>
            <p style={{ fontSize: 12, color: "#666" }}>
              Будь-хто з цим лінком зможе побачити аудит без логіну.
            </p>
            <input readOnly value={shareModal.url} style={{
              width: "100%", padding: 8, fontSize: 11, border: "1px solid " + t.border,
              borderRadius: 4, fontFamily: "monospace", marginBottom: 10,
            }} />
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={copyShareUrl} style={{ ...btnP, fontSize: 12, display: "flex", alignItems: "center", gap: 6 }}>
                {Icons.copy(14)} Копіювати
              </button>
              <button onClick={() => setShareModal(null)} style={{ ...btnG, fontSize: 12 }}>Закрити</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
